import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import RecentActivity from './RecentActivity';
import DriverFoundNotification from './DriverFoundNotification';
import DashboardActions from './DashboardActions';
import driverApi from '../../clients/DriverClient';
import { MatchDriverResponse } from '../../models/MatchDriverResponse';
import identityApi from '../../clients/IdentityClient';
import TabControl, { Tab } from '../TabControl';
import DriverLeaderboardsView from './DriverLeaderboardsView';
import CareerView from './CareerView';
import RaceAroundWorldView from './RaceAroundWorldView';
import { DriverUsedFacility } from '../../models/RaceData';

const DriverDashboard: React.FC = () => {
  const { user } = useAuth();
  const [foundDriver, setFoundDriver] = useState<MatchDriverResponse | null>(null);
  const [hasCheckedForDriver, setHasCheckedForDriver] = useState(false);
  const [facilities, setFacilities] = useState<DriverUsedFacility[]>([]);
  const [selectedFacility, setSelectedFacility] = useState<DriverUsedFacility | null>(null);
  const [isLoadingFacilities, setIsLoadingFacilities] = useState(false);
  const [facilitiesError, setFacilitiesError] = useState('');

  useEffect(() => {
    const checkForDriver = async () => {
      if (user && !user.driverId && !hasCheckedForDriver && user.firstName && user.lastName && user.email) {
        setHasCheckedForDriver(true);

        try {
          const driverMatch = await driverApi.findDriverByUserData({
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName
          });

          if (driverMatch !== null) {
            const dismissedDriverId = localStorage.getItem('dismissedDriverId');
            if (dismissedDriverId !== driverMatch.driverId) {
              setFoundDriver(driverMatch);
            }
          }
        } catch (error) {
          console.error('Error checking for driver:', error);
        }
      }
    };

    checkForDriver();
  }, [user, hasCheckedForDriver]);

  // Load facilities when driverId is available
  useEffect(() => {
    const fetchFacilities = async () => {
      if (!user?.driverId) return;
      setIsLoadingFacilities(true);
      setFacilitiesError('');
      try {
        const result = await driverApi.getDriverUsedFacilities(user.driverId);
        if (result) {
          // Sort facilities by track friendly name
          const sortedFacilities = result.sort((a, b) => 
            a.trackFriendlyName.localeCompare(b.trackFriendlyName)
          );
          setFacilities(sortedFacilities);
          // Auto-select first facility if none selected
          if (sortedFacilities.length > 0) {
            setSelectedFacility((currentFacility) => currentFacility || sortedFacilities[0]);
          }
        }
      } catch (e) {
        setFacilitiesError('Failed to load facilities');
      } finally {
        setIsLoadingFacilities(false);
      }
    };
    fetchFacilities();
  }, [user?.driverId]);

  const handleConnectDriver = async () => {
    if (!user?.id || !foundDriver?.driverId) {
      console.error('User ID is not set');
      return;
    }

    console.log('Connecting driver:', foundDriver?.driverId);
    await identityApi.linkUserToDriver({ driverId: foundDriver?.driverId, userId: user.id });
    localStorage.setItem('driverUser', JSON.stringify({ ...user, driverId: foundDriver?.driverId }));
    user.driverId = foundDriver?.driverId;

    await identityApi.refreshToken();
    localStorage.setItem('authToken', await identityApi.refreshToken());
    setFoundDriver(null);
  };

  const handleCloseNotification = () => {
    if (foundDriver?.driverId) {
      localStorage.setItem('dismissedDriverId', foundDriver.driverId);
    }
    setFoundDriver(null);
  };

  const handleFacilityChange = (facilityKey: string) => {
    const facility = facilities.find(f => `${f.track}-${f.trackLayout}-${f.car}` === facilityKey);
    setSelectedFacility(facility || null);
  };

  // Create tabs for the tab control
  const tabs: Tab[] = [
  {
      id: 'race-around-world',
      title: 'Race Around the World',
      content: <RaceAroundWorldView driverId={user?.driverId || undefined} />
    },
    {
      id: 'leaderboards',
      title: 'Leaderboards',
      content: <DriverLeaderboardsView
        driverId={user?.driverId || undefined}
        facilities={facilities}
        selectedFacility={selectedFacility}
        isLoadingFacilities={isLoadingFacilities}
        facilitiesError={facilitiesError}
        onFacilityChange={handleFacilityChange}
      />
    },
    {
      id: 'recent-activity',
      title: 'Recent Activity',
      content: <RecentActivity driverId={user?.driverId || undefined} take={10} />
    },
    {
      id: 'career',
      title: 'Career',
      content: <CareerView driverId={user?.driverId || undefined} />
    }
  ];

  return (
    <>
      <div className="container driver-dashboard">
        <div className="driver-info">
          <DashboardActions
            driverId={user?.driverId || undefined}
            firstName={user?.firstName}
          />
        </div>
        {!user?.driverId && foundDriver ? (
          <DriverFoundNotification
            driverData={foundDriver}
            onConnect={handleConnectDriver}
            onClose={handleCloseNotification}
          />
        ) : (
          <TabControl tabs={tabs} defaultTabId="race-around-world" />
        )}
      </div>
    </>
  );
};

export default DriverDashboard;